import { Button } from "@/src/components/buttons";
import { CompartmentCard } from "@/src/components/cards";
import { ListConstructor } from "@/src/components/fetchableLists";
import { ReferenceTile } from "@/src/components/referenceTile";
import { Input } from "@/src/components/universalInput";
import {
  FieldAnswer,
  Patient,
  RefAnswer,
  Reference,
  ReferenceCategory,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch, { sleep } from "@/src/utils/apiFetch";
import {
  getItemId,
  getUserId,
  jsonAbstract,
  openToast,
  shouldOpenInDrawer,
  uid,
} from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { SnomedConceptsList } from "../snomed/SnomedConceptPicker";
import { openDrawer } from "@/src/components/dialogs";
import { RefAnswerForm } from "../fields/referenceFieldsForm";
import { FamilyAntecedentsCompartment } from "./familyAntecedentsCompartment";

function AntecedentsDrawer({
  patient,
  antecedents,
  onAntecedentsChange,
}: {
  patient: Patient | null | undefined;
  antecedents: RefAnswer[];
  onAntecedentsChange: (newValue: RefAnswer[]) => void;
}) {
  const [initialAntecedents, setInitialAntecedents] = useState(antecedents);
  const allowedCategories: { id: ReferenceCategory; name: string }[] = [
    { id: "riskFactor", name: "Facteurs de risques" },
    { id: "physiologicalAntecedent", name: "Antécédents physiologiques" },
    { id: "personalAntecedent", name: "Antécédents pathologiques" },
    { id: "surgicalAntecedent", name: "Antécédents chirurgicaux" },
    // { id: "familialAntecedent", name: "Antécédents Familiaux" },
    // { id: "toxicAntecedent", name: "Antécédents toxiques" },
  ];

  return (
    <div id="antecedents_drawer" className={`grid mc1 gap20`}>
      {allowedCategories.map((category) => {
        return (
          <AntecedentCompartment
            key={uid()}
            patient={patient}
            current={initialAntecedents}
            category={category}
            onAntecedentsChange={(newValue) => {
              setInitialAntecedents(newValue);
              onAntecedentsChange(newValue);
              console.log("gonna set", newValue);
            }}
          ></AntecedentCompartment>
        );
      })}
      {patient && (
        <FamilyAntecedentsCompartment
          originalPatient={patient}
          onAntecedentsChange={() => {
            onAntecedentsChange(initialAntecedents);
          }}
        ></FamilyAntecedentsCompartment>
      )}
    </div>
  );
}

export function FamilyMemberAntecedentsDrawer({
  patient,
  antecedents,
  onAntecedentsChange,
}: {
  patient: Patient | null | undefined;
  antecedents: RefAnswer[];
  onAntecedentsChange: (newValue: RefAnswer[]) => void;
}) {
  const [initialAntecedents, setInitialAntecedents] = useState(antecedents);
  const allowedCategories: { id: ReferenceCategory; name: string }[] = [
    { id: "personalAntecedent", name: "Antécédents pathologiques" },
    { id: "surgicalAntecedent", name: "Antécédents chirurgicaux" },
  ];

  return (
    <div id="antecedents_drawer" className={`c1 mc1 gap20`}>
      {patient != null && (
        <Input
          name="Notes générales"
          value={patient?.notes}
          shouldFocus={true}
          type="textarea"
          rows={5}
          onChange={(newValue) => {
            patient!.notes = newValue;
          }}
        ></Input>
      )}
      {allowedCategories.map((category) => {
        return (
          <AntecedentCompartment
            key={uid()}
            patient={patient}
            current={initialAntecedents}
            category={category}
            onAntecedentsChange={(newValue) => {
              setInitialAntecedents(newValue);
              onAntecedentsChange(newValue);
            }}
          ></AntecedentCompartment>
        );
      })}
    </div>
  );
}

function AntecedentCompartment({
  patient,
  current,
  category,
  onAntecedentsChange,
}: {
  patient: Patient | null | undefined;
  current: (RefAnswer | null | undefined)[];
  category: { id: ReferenceCategory; name: string };
  onAntecedentsChange: (newValue: RefAnswer[]) => void;
}) {
  const [refAnswers, setRefAnswers] = useState(current || []);
  const [searchKey, setSearchKey] = useState("");
  const [reload, setReload] = useState(0);
  let frequentReferences =
    USER?.get()?.speciality?.frequentReferences?.filter(
      (reference) => reference?.category == category.id
    ) || [];

  let { data, isLoading, isError, error } = useQuery({
    queryKey: ["AntecedentCompartment", searchKey, category.id, reload],
    queryFn: () => {
      if (!searchKey) return frequentReferences;

      // return frequentReferences.filter((reference) =>
      //   reference?.name?.toLowerCase()?.includes(searchKey?.toLowerCase())
      // );

      return apiFetch(
        "references",
        "loadMany",
        {
          userId: getUserId(),
          categoriesIds: [category.id],
          showAll: true,
          searchKey: searchKey?.toLowerCase(),
        },
        "Erreur lors du chargement des références"
      );
    },
  });

  if (!frequentReferences?.length) return null;

  let references = data || [];

  function getMandatoryReferences(
    existingReferences: (Reference | null | undefined)[]
  ): Reference[] {
    let frequentReferences =
      USER?.get()?.speciality?.frequentReferences?.filter(
        (reference) => reference?.category == category.id
      );

    return current
      ?.filter((refAnswer) => {
        return (
          refAnswer?.reference?.category == category.id &&
          !frequentReferences?.find(
            (freq) => freq?.id == refAnswer?.reference?.id
          )
        );
      })
      .filter((refAnswer) => {
        return !existingReferences.find(
          (reference) => reference?.id == refAnswer?.reference?.id
        );
      })
      .map((refAnswer) => {
        return refAnswer?.reference;
      }) as Reference[];
  }

  async function addRefAnswer(reference: Reference, refAnswers: RefAnswer[]) {
    const newRefAnswers = [...refAnswers];
    const newRefAnswerId = `refAnswers:${reference.id?.replace(
      ":",
      ""
    )}${patient?.id?.replace(":", "")}`;

    if (!newRefAnswers.find((item) => getItemId(item) == newRefAnswerId))
      newRefAnswers.push({
        id: newRefAnswerId,
        patient,
        reference: reference,
        fieldAnswers: [],
        creator: USER?.get(),
      });

    setRefAnswers(newRefAnswers);
    onAntecedentsChange(newRefAnswers);
    if (shouldOpenInDrawer(reference)) {
      openFormInDrawer({
        refAnswer: newRefAnswers.find(
          (antecedent) => antecedent?.reference?.id == reference?.id
        ),
        patient,
      });
    }
  }

  async function removeRefAnswer(
    reference: Reference,
    refAnswers: RefAnswer[]
  ) {
    let newRefAnswers = [...refAnswers];
    let currentAntecedent = newRefAnswers.find(
      (antecedent) => antecedent?.reference?.id == reference.id
    );

    newRefAnswers = newRefAnswers.filter(
      (antecedent) => antecedent?.reference?.id != reference.id
    );

    setRefAnswers([...newRefAnswers]);
    onAntecedentsChange([...newRefAnswers]);

    if (currentAntecedent?.id) {
      try {
        let result = await apiFetch(
          "refAnswers",
          "deleteOne",
          {
            id: currentAntecedent.id,
          },
          "Erreur lors de la suppression de l'antécédent"
        );
        setReload((prev) => prev + 1);
      } catch (e) {
        newRefAnswers.push(currentAntecedent);
        setRefAnswers(newRefAnswers);
        onAntecedentsChange(newRefAnswers);
        throw e;
      }
    }
  }

  async function openFormInDrawer({
    refAnswer,
    patient,
  }: {
    refAnswer: RefAnswer | null | undefined;
    patient: Patient | null | undefined;
  }) {
    if (!patient) return openToast("error", "No patient found");
    if (!refAnswer) return openToast("error", "No refAnswer found");

    await openDrawer({
      id: "referenceFieldsDrawer",
      title: `Détails ${refAnswer?.reference?.name}`,
      additionalClasses: ["medium"],
      content: (
        <RefAnswerForm
          patient={patient}
          refAnswer={refAnswer}
          onChange={(newPatientsReference) => {
            refAnswer = newPatientsReference;
          }}
        ></RefAnswerForm>
      ),
      onConfirm: async () => {
        if (!refAnswer) return;

        setRefAnswers((prevAnswers) => {
          const newAnswers = prevAnswers.map((a) => {
            if (!a) return null;
            if (a?.id === refAnswer?.id) {
              return {
                ...a,
                fieldAnswers: refAnswer?.fieldAnswers || [],
                notes: refAnswer?.notes || "",
              };
            }
            return a;
          });
          onAntecedentsChange(newAnswers.filter((answer) => answer != null));
          return newAnswers;
        });

        setReload((prev) => prev + 1);
      },
    });
  }

  function getTiles(references: (Reference | null | undefined)[]) {
    return references?.map((reference) => {
      if (!reference) return null;
      let parent = refAnswers?.find(
        (refAnswer) => refAnswer?.reference?.id == reference?.id
      );

      let refAnswer = refAnswers?.find(
        (antecedent) => antecedent?.reference?.id == reference?.id
      );

      return (
        <ReferenceTile
          key={uid()}
          patient={patient}
          reference={reference}
          searchKey={searchKey}
          isInitiallyActive={parent != null}
          fieldAnswers={refAnswer?.fieldAnswers}
          onExpansionChange={async (isExpanded) => {
            if (shouldOpenInDrawer(reference))
              await openFormInDrawer({
                refAnswer,
                patient,
              });
          }}
          onActiveChange={async (isActive) => {
            if (isActive) {
              await addRefAnswer(
                reference,
                refAnswers.filter((answer) => answer) as RefAnswer[]
              );
            } else {
              await removeRefAnswer(
                reference,
                refAnswers.filter((answer) => answer) as RefAnswer[]
              );
            }
          }}
          onChange={(field, newValue) => {
            let refAnswer = refAnswers?.find(
              (antecedent) => antecedent?.reference?.id == reference.id
            );
            if (!refAnswer) return;
            refAnswer.fieldAnswers ||= [];

            let defaultFieldAnswer: FieldAnswer = {
              field,
              parent: jsonAbstract(parent?.id),
              patient,
              creator: jsonAbstract(USER?.get()?.id),
            };

            let currentFieldAnswer: FieldAnswer =
              refAnswer.fieldAnswers.find(
                (fieldAnswer) => fieldAnswer?.field?.id == field.id
              ) || defaultFieldAnswer;

            currentFieldAnswer.value = newValue;

            refAnswer.fieldAnswers = refAnswer.fieldAnswers.filter(
              (fieldAnswer) => fieldAnswer?.field?.id != field.id
            );

            refAnswer.fieldAnswers.push(currentFieldAnswer);
          }}
        >
          <Input
            name={"Détails (optionnel)"}
            value={refAnswer?.notes}
            type={"textarea"}
            placeholder={"Texte libre..."}
            rows={5}
            onChange={(newValue) => {
              if (!refAnswer) return;
              refAnswer.notes = newValue;
            }}
          />
        </ReferenceTile>
      );
    });
  }

  function shouldShowSnomedList() {
    // return false;
    return (
      searchKey != "" &&
      category.id != "riskFactor" &&
      category.id != "physiologicalAntecedent"
    );
  }

  return (
    <CompartmentCard
      title={<div className="pleft15">{category.name}</div>}
      searchKey={searchKey}
      onSearch={(newValue) => {
        setSearchKey(newValue || "");
      }}
    >
      {!shouldShowSnomedList() && (
        <ListConstructor
          data={[...(data || []), ...getMandatoryReferences(references)]}
          isLoading={isLoading}
          isError={isError}
          error={error}
          emptyChild={
            <div className="c1 p30 center gap10">
              <div className="gray">Aucun élément trouvé</div>
              <Button onClick={async () => {}}>Créer {searchKey}</Button>
            </div>
          }
        >
          <div className="grid c1 gap10">
            {getTiles(getMandatoryReferences(references))}
            {getTiles(references)}
          </div>
        </ListConstructor>
      )}
      {shouldShowSnomedList() && (
        <SnomedConceptsList
          key={`snomed-${category.id}-${searchKey}`}
          category={category.id}
          parentIds={[
            category.id == "surgicalAntecedent" ? "procedure" : "disease",
          ]}
          currentSearchKey={searchKey}
          showSearchBar={false}
          onSelect={async (reference) => {
            const alreadyAdded = refAnswers.find(
              (answer) => getItemId(answer?.reference) == getItemId(reference)
            );
            console.log({ alreadyAdded, reference });
            if (!alreadyAdded)
              await addRefAnswer(
                reference,
                refAnswers.filter((answer) => answer) as RefAnswer[]
              );
            setSearchKey("");
          }}
        ></SnomedConceptsList>
      )}
    </CompartmentCard>
  );
}

export { AntecedentCompartment, AntecedentsDrawer };
